import { memo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { ArrowRightOutlined, CalendarOutlined, DownOutlined, FileTextOutlined, LinkOutlined, SendOutlined } from "@ant-design/icons";
import CreatorActionButton from "./CreatorActionButton";
import CreatorLinkPreview from "./CreatorLinkPreview";
import ThemeShowcase from "./ThemeShowcase";
import BookMePreview from "../Booking/BookMePreview";
import FaqList from "./FaqList";
import { homeFaqs } from "./publicSiteContent";
import { closingPageThemes, creatorPageThemes, creatorTools, homeTestimonials } from "./homeCreatorContent";
import { trackClarityEvent } from "../../utils/clarityAnalytics";
import styles from "./HomeCreatorLanding.module.scss";

const PreviewLoading = () => <span role="status" className={styles.previewLoading}>Loading preview…</span>;
const CreatorWritingPreview = dynamic(() => import("./CreatorWritingPreview"), { loading: PreviewLoading });
const CreatorSocialPreview = dynamic(() => import("./CreatorSocialPreview"), { loading: PreviewLoading });
const toolIcons = { links: LinkOutlined, bookings: CalendarOutlined, paidSubscriptions: FileTextOutlined, publish: SendOutlined };

function CreatorTools() {
  const [active, setActive] = useState("publish");
  const toolRefs = useRef([]);
  const current = creatorTools.find(tool => tool.key === active);
  const select = (key) => {
    setActive(key);
    trackClarityEvent(`home_tool_${key}_viewed`);
  };
  const handleKeys = (event, index) => {
    let next;
    if (["ArrowDown", "ArrowRight"].includes(event.key)) next = (index + 1) % creatorTools.length;
    else if (["ArrowUp", "ArrowLeft"].includes(event.key)) next = (index + creatorTools.length - 1) % creatorTools.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = creatorTools.length - 1;
    else return;
    event.preventDefault();
    select(creatorTools[next].key);
    toolRefs.current[next]?.focus();
  };
  return <section id="creator-tools" className={styles.section} aria-labelledby="creator-tools-title">
    <div className={styles.container}>
      <h2 id="creator-tools-title">Choose what you want to create.</h2>
      <div className={styles.toolLayout}>
        <div className={styles.toolList} role="group" aria-label="Creator tools">
          {creatorTools.map((tool, index) => {
            const Icon = toolIcons[tool.key];
            const selected = active === tool.key;
            return <div key={tool.key} className={styles.toolItem} data-active={selected}>
              <button type="button" id={`creator-tool-${tool.key}`} aria-controls={`creator-panel-${tool.key}`}
                aria-pressed={selected} ref={node => { toolRefs.current[index] = node; }}
                className={styles.toolChoice} onClick={() => select(tool.key)} onKeyDown={event => handleKeys(event, index)}>
                <span className={styles.toolIcon}><Icon aria-hidden="true" /></span>
                <span className={styles.toolCopy}>
                  <strong><span className={styles.fullToolTitle}>{tool.title}</span><span className={styles.compactToolTitle}>{tool.shortTitle}</span></strong>
                  <span>{tool.description}</span>
                </span>
              </button>
              {selected ? <CreatorActionButton tool={tool.key} source="home_tool" type="link" className={styles.toolAction}>
                {tool.action}<ArrowRightOutlined aria-hidden="true" />
              </CreatorActionButton> : null}
            </div>;
          })}
        </div>
        <p className={styles.mobileToolDescription}>{current.description}</p>
        <div className={styles.toolStage}>
          {creatorTools.map(tool => <div key={tool.key} role="region" id={`creator-panel-${tool.key}`}
            aria-labelledby={`creator-tool-${tool.key}`} tabIndex={0} hidden={tool.key !== active}
            className={styles.toolPanel}>
            {tool.key === active ? <div className={styles.panelContent} key={active}>
              {active === "links" ? <div className={styles.singleLinkPreview}><CreatorLinkPreview preset={creatorPageThemes[2]} /></div>
                : active === "bookings" ? <BookMePreview onSelect={() => trackClarityEvent("home_booking_service_selected")} />
                  : active === "paidSubscriptions" ? <CreatorWritingPreview /> : <CreatorSocialPreview />}
            </div> : null}
          </div>)}
        </div>
      </div>
      <span className={styles.srOnly} aria-live="polite">{current.title}</span>
    </div>
  </section>;
}

function HomeCreatorLanding() {
  return <main className={styles.landing}>
    <section className={`${styles.hero} ${styles.container}`} aria-labelledby="creator-home-title">
      <div className={styles.heroCopy}>
        <h1 id="creator-home-title">One place for your links, services, and <span className={styles.heroEmphasis}>paid articles.</span></h1>
        <p className={styles.lead}>Create a free link-in-bio page, offer your services, and write for paying subscribers. Schedule social posts from the same account.</p>
        <div className={styles.heroActions}>
          <CreatorActionButton source="home_hero" size="large" className={styles.primaryAction} />
          <Link href="#creator-tools" className={styles.textLink}>Explore the tools<ArrowRightOutlined aria-hidden="true" /></Link>
        </div>
        <p className={styles.reassurance}>Free to start. No credit card needed to sign up.</p>
      </div>
      <ThemeShowcase />
    </section>
    <CreatorTools />
    <section className={styles.testimonialsSection} aria-labelledby="creator-testimonials-title">
      <div className={`${styles.container} ${styles.testimonials}`}>
        <h2 id="creator-testimonials-title" className={styles.srOnly}>What people say about BonJourno</h2>
        {homeTestimonials.map(({ name, quote, excerpt }) => <figure className={styles.testimonial} key={name} aria-label={`Review from ${name}`}>
          <details className={styles.fullTestimonial}>
            <summary aria-label={`Full review from ${name}`}>
              <span className={styles.testimonialAuthor}>{name}</span>
              <span className={styles.testimonialToggle}>Full review<DownOutlined aria-hidden="true" /></span>
            </summary>
            <blockquote><p>“{quote}”</p></blockquote>
          </details>
          <blockquote className={styles.testimonialExcerpt}><p>“{excerpt}”</p></blockquote>
        </figure>)}
      </div>
    </section>
    <section className={styles.section} aria-labelledby="creator-faq-title">
      <div className={`${styles.container} ${styles.faqLayout}`}>
        <h2 id="creator-faq-title">A few things before you start.</h2>
        <div className={styles.homeFaq}>
          <FaqList items={homeFaqs} headingLevel={3} />
        </div>
        <div className={styles.faqLinks}>
          <Link href="/FAQ" prefetch={false} className={styles.textLink}>Read all FAQs<ArrowRightOutlined aria-hidden="true" /></Link>
          <Link href="/pricing" prefetch={false} className={styles.textLink}>See pricing and limits<ArrowRightOutlined aria-hidden="true" /></Link>
        </div>
      </div>
    </section>
    <section className={`${styles.container} ${styles.closing}`} aria-labelledby="creator-start-title">
      <div className={styles.closingCopy}>
        <h2 id="creator-start-title">Your work.<br />One link away.</h2>
        <p>Bring your links, services, and articles together on a page you can share.</p>
        <CreatorActionButton source="home_final" size="large" className={styles.primaryAction}>
          Create your free page<ArrowRightOutlined aria-hidden="true" />
        </CreatorActionButton>
        <p className={styles.reassurance}>Free to start. No credit card needed.</p>
      </div>
      <ThemeShowcase presets={closingPageThemes} decorative className={styles.closingShowcase} />
    </section>
  </main>;
}

export default memo(HomeCreatorLanding);
